import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Users, Building2, Wrench } from "lucide-react";
import { MaskedLines, Reveal, Eyebrow, EASE } from "@/components/motion";
import { BrandMarquee } from "@/components/Marquee";
import { BRANDS, VALUES, LINKEDIN_JOBS_URL } from "@/lib/api";

const HERO_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_dpo-plumbing/artifacts/393cbb11e8c9f001_f4834b68b1ba8ffcaed9df9299dabc53.jpeg";
const TECH_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_dpo-plumbing/artifacts/35ac9df8db2999cb_706035829_1602985265164366_4292849102337608859_n.webp";
const ROOF_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_dpo-plumbing/artifacts/bdd0765dbfbed04f_images.jpeg";

const STATS = [
  { icon: Users, value: "700+", label: "Employees across the platform" },
  { icon: Building2, value: "13", label: "Trusted partner brands" },
  { icon: Wrench, value: "3", label: "Verticals: HVAC, Plumbing, Mechanical" },
];

const PILLARS = [
  { title: "Leadership", desc: "Experienced operators who have built and scaled service businesses in the trades." },
  { title: "Technology", desc: "Modern systems for dispatch, finance, and reporting that free teams to serve customers." },
  { title: "Training", desc: "Career pathways and continuous development for technicians, installers, and managers." },
  { title: "Support", desc: "Centralized recruiting, procurement, marketing, and back-office horsepower." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(2,132,199,0.10),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(234,88,12,0.08),transparent_55%)]" aria-hidden="true" />
        <div className="relative px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              <Eyebrow>DPO&amp;Co HVAC &amp; Plumbing</Eyebrow>
            </motion.div>
            <MaskedLines
              lines={["Great businesses are", "powered by", "great people."]}
              delay={0.15}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04] text-navy"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
              data-testid="hero-subtext"
            >
              DPO HVAC Mechanical Services is a rapidly growing platform of residential and commercial
              HVAC, plumbing, and mechanical service companies - providing the leadership, resources,
              technology, and support that help great companies and great people grow.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/partner"
                data-testid="hero-partner-cta"
                className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-ember/25 transition-all duration-200 hover:bg-ember-bright hover:gap-3"
              >
                Partner Your Business <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={LINKEDIN_JOBS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-careers-cta"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:border-hydro hover:text-hydro"
              >
                View Open Roles
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
              className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4"
              data-testid="hero-stats"
            >
              {STATS.map((s) => (
                <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                  <s.icon className="h-5 w-5 text-ember" aria-hidden="true" />
                  <p className="mt-3 font-display text-3xl font-extrabold text-navy">{s.value}</p>
                  <p className="mt-1 text-xs sm:text-sm text-slate-500">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="relative hidden lg:block"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <img src={HERO_IMG} alt="Prime Plumbing service van, part of the DPO HVAC family of brands" fetchpriority="high" decoding="async" className="w-full rounded-3xl object-cover aspect-[16/10] shadow-2xl" />
              <div className="absolute -bottom-8 -left-8 w-48 rounded-2xl border-4 border-white shadow-xl overflow-hidden">
                <img src={ROOF_IMG} alt="Large-scale commercial HVAC ductwork installed by a DPO platform company" loading="lazy" decoding="async" className="w-full aspect-square object-cover" />
              </div>
              <div className="absolute -top-5 -right-5 h-24 w-24 rounded-full bg-gradient-to-br from-ember to-hydro opacity-90" aria-hidden="true" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <BrandMarquee />

      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32" data-testid="home-about-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <img src={TECH_IMG} alt="The ComfortMaster Heating & Air team, part of the DPO HVAC family of brands" loading="lazy" decoding="async" className="w-full rounded-2xl object-cover aspect-[4/3] shadow-xl" />
          </Reveal>
          <div>
            <Reveal><Eyebrow>Who we are</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
                Building the future of the trades through people, performance, and partnership.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                With 700+ employees and a growing family of trusted brands, DPO HVAC preserves the local
                culture and reputation that made each company successful - while adding the resources of a
                national platform behind every name on every truck.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/about" data-testid="home-about-link" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-hydro transition-all hover:gap-3">
                More about DPO HVAC <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-navy" data-testid="home-pillars-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.25em] font-semibold text-ember-bright">What the platform provides</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white lg:sticky lg:top-32">
                  One platform.<br />Four engines<br />of growth.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className={`group flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8 py-8 ${i > 0 ? "border-t border-slate-700/60" : ""}`}>
                    <p className="font-mono text-sm font-semibold text-ember-bright shrink-0">0{i + 1}</p>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-white sm:w-56 shrink-0 transition-transform duration-300 group-hover:translate-x-1">{p.title}</h3>
                    <p className="text-sm sm:text-base leading-relaxed text-slate-400">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-ember to-ember-bright" data-testid="home-values-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32">
          <Reveal>
            <p className="font-mono text-sm uppercase tracking-[0.25em] font-semibold text-white/90">Our core values</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-10">
            {VALUES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.07}>
                <div className="border-t-2 border-white/50 pt-5">
                  <h3 className="font-display text-xl font-bold text-white">{v.name}</h3>
                  <p className="mt-3 text-base leading-relaxed text-white/90">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32" data-testid="home-brands-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Our family of brands</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy lg:sticky lg:top-32">
                Local names.<br />National strength.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {BRANDS.slice(0, 5).map((b, i) => (
              <Reveal key={b.name} delay={i * 0.05}>
                <div className={`group flex items-center justify-between gap-4 py-5 ${i > 0 ? "border-t border-slate-200" : ""}`} data-testid={`home-brand-row-${i}`}>
                  <div className="flex items-center gap-5">
                    {b.logo ? (
                      <img src={b.logo} alt={`${b.name} logo`} loading="lazy" decoding="async" className="h-10 w-14 shrink-0 object-contain" />
                    ) : (
                      <span className="font-mono text-xs font-semibold text-slate-400 w-14 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    )}
                    <div>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-navy transition-colors duration-200 group-hover:text-ember">{b.name}</h3>
                      <p className="mt-0.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">{b.tag}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-300 transition-all duration-200 group-hover:text-ember group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <Link to="/companies" data-testid="home-companies-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-hydro transition-all hover:gap-3">
                See all 13 platform companies <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50" data-testid="home-cta-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-2xl bg-navy p-8 sm:p-10 shadow-lg">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Own an HVAC or plumbing business?</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-300">
                Partner with a platform that protects your legacy, rewards your team, and funds your next stage of growth.
              </p>
              <Link to="/partner" data-testid="cta-partner-button" className="mt-7 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ember-bright">
                Why Partner With Us <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl bg-white border border-slate-200 p-8 sm:p-10 shadow-sm">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-navy">Build your career in the trades.</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Join 700+ people across our family of brands - with training, advancement, and a culture that puts people first.
              </p>
              <Link to="/join" data-testid="cta-join-button" className="mt-7 inline-flex items-center gap-2 rounded-full bg-hydro px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-hydro-glow hover:text-navy">
                Why Join Us <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
